import type { NetworkSample } from '@calling/shared';

/** Running totals from the previous sample, needed to turn counters into rates. */
export interface StatTotals {
  at: number;
  bytesSent: number;
  bytesReceived: number;
  /** Per incoming stream (by SSRC): packets received and lost so far. */
  streams: Record<string, { received: number; lost: number }>;
}

export interface CallStats {
  sample: NetworkSample;
  kbpsOut: number;
  kbpsIn: number;
  /** True when media flows through the TURN relay; null when unknown. */
  relay: boolean | null;
  sendResolution: string | null;
  receiveResolution: string | null;
  codec: string | null;
  /** The device, not the network, is limiting outgoing video (E6-S2). */
  cpuLimited: boolean;
  dataUsedBytes: number;
}

type Report = Map<string, Record<string, unknown>> | Iterable<[string, Record<string, unknown>]>;

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

function entries(report: Report | null | undefined): Array<Record<string, unknown>> {
  if (!report) return [];
  const out: Array<Record<string, unknown>> = [];
  // RTCStatsReport is map-like; forEach is the one method every browser has.
  (report as { forEach: (cb: (value: Record<string, unknown>) => void) => void }).forEach((value) => out.push(value));
  return out;
}

function selectedPair(stats: Array<Record<string, unknown>>): Record<string, unknown> | null {
  const transport = stats.find((s) => s.type === 'transport' && typeof s.selectedCandidatePairId === 'string');
  if (transport) {
    const pair = stats.find((s) => s.id === transport.selectedCandidatePairId);
    if (pair) return pair;
  }
  return stats.find((s) => s.type === 'candidate-pair' && (s.selected === true || (s.nominated === true && s.state === 'succeeded'))) ?? null;
}

/**
 * Turns the browser's WebRTC statistics for the sending and receiving
 * transports into the few numbers the app acts on (E6-S3, E6-S8).
 */
export function readStats(send: Report | null, recv: Report | null, prev: StatTotals | null, at: number): { stats: CallStats; totals: StatTotals } {
  const s = entries(send);
  const r = entries(recv);
  const sendPair = selectedPair(s);
  const recvPair = selectedPair(r);

  const bytesSent = num(sendPair?.bytesSent) ?? 0;
  const bytesReceived = num(recvPair?.bytesReceived) ?? 0;
  const streams: StatTotals['streams'] = {};
  let recvW = 0;
  let recvH = 0;
  for (const stat of r) {
    if (stat.type !== 'inbound-rtp') continue;
    // The SFU also sends a padding-only "probator" stream to measure
    // bandwidth. It carries no media and its counters are meaningless, so
    // only streams that actually decode something are counted.
    const carriesMedia = stat.kind === 'audio' || (num(stat.framesDecoded) ?? 0) > 0 || (num(stat.frameHeight) ?? 0) > 0;
    if (!carriesMedia || stat.mid === 'probator') continue;
    streams[String(stat.ssrc ?? stat.id)] = { received: num(stat.packetsReceived) ?? 0, lost: num(stat.packetsLost) ?? 0 };
    if ((num(stat.frameHeight) ?? 0) > recvH) {
      recvW = num(stat.frameWidth) ?? 0;
      recvH = num(stat.frameHeight) ?? 0;
    }
  }

  let sendW = 0;
  let sendH = 0;
  let cpuLimited = false;
  let codecId: string | null = null;
  let remoteLoss: number | null = null;
  let remoteRtt: number | null = null;
  for (const stat of s) {
    if (stat.type === 'outbound-rtp' && stat.kind === 'video') {
      if (stat.qualityLimitationReason === 'cpu') cpuLimited = true;
      if ((num(stat.frameHeight) ?? 0) > sendH) {
        sendW = num(stat.frameWidth) ?? 0;
        sendH = num(stat.frameHeight) ?? 0;
      }
      if (typeof stat.codecId === 'string') codecId = stat.codecId;
    } else if (stat.type === 'remote-inbound-rtp') {
      const fraction = num(stat.fractionLost);
      if (fraction !== null) remoteLoss = Math.max(remoteLoss ?? 0, fraction * 100);
      const rtt = num(stat.roundTripTime);
      if (rtt !== null) remoteRtt = Math.max(remoteRtt ?? 0, rtt * 1000);
    }
  }
  const codecStat = codecId ? s.find((x) => x.id === codecId) : null;
  const codec = typeof codecStat?.mimeType === 'string' ? codecStat.mimeType.split('/')[1]! : null;

  const seconds = prev ? Math.max(0.001, (at - prev.at) / 1000) : 0;
  const rate = (now: number, before: number | undefined) => (prev && seconds > 0 ? Math.max(0, ((now - (before ?? 0)) * 8) / 1000 / seconds) : 0);
  const kbpsOut = rate(bytesSent, prev?.bytesSent);
  const kbpsIn = rate(bytesReceived, prev?.bytesReceived);

  // Loss over the last interval, as experienced on the receiving side; when
  // nothing is being received, fall back to what the far end reports.
  let lossPct = remoteLoss ?? 0;
  if (prev) {
    let got = 0;
    let lost = 0;
    for (const [ssrc, now] of Object.entries(streams)) {
      const before = prev.streams[ssrc];
      if (!before) continue;
      got += Math.max(0, now.received - before.received);
      // The counter can step backwards when a late packet arrives; that is not loss.
      lost += Math.max(0, now.lost - before.lost);
    }
    if (got + lost > 0) lossPct = (lost / (got + lost)) * 100;
  }

  const rttSec = num(sendPair?.currentRoundTripTime) ?? num(recvPair?.currentRoundTripTime);
  const rttMs = rttSec !== null ? rttSec * 1000 : (remoteRtt ?? 0);

  const outgoing = num(sendPair?.availableOutgoingBitrate);
  const incoming = num(recvPair?.availableIncomingBitrate);
  const available = outgoing ?? incoming;

  const pair = sendPair ?? recvPair;
  const local = pair && typeof pair.localCandidateId === 'string' ? [...s, ...r].find((x) => x.id === pair.localCandidateId) : null;
  const relay = local && typeof local.candidateType === 'string' ? local.candidateType === 'relay' : null;

  return {
    stats: {
      sample: { at, availableKbps: available !== null ? available / 1000 : null, lossPct, rttMs },
      kbpsOut,
      kbpsIn,
      relay,
      sendResolution: sendH > 0 ? `${sendW}×${sendH}` : null,
      receiveResolution: recvH > 0 ? `${recvW}×${recvH}` : null,
      codec,
      cpuLimited,
      dataUsedBytes: bytesSent + bytesReceived,
    },
    totals: { at, bytesSent, bytesReceived, streams },
  };
}
